/**
 * SalonHub トークンブラックリストサービス
 * JWT リボーク・無効化管理システム
 *
 * @description JWT ID (JTI) ベースのトークンリボーク機能
 * @security メモリ + LocalStorage + Redis準備
 * @performance <10ms ブラックリストチェック
 */

import {
  revokeJTI,
  isJTIRevoked
} from './crypto';
import logger from './logger';

// ブラックリスト管理エラー
export class TokenBlacklistError extends Error {
  constructor(
    message: string,
    public code: 'BLACKLIST_FAILED' | 'REVOCATION_FAILED' | 'LOOKUP_FAILED' | 'CLEANUP_FAILED'
  ) {
    super(message);
    this.name = 'TokenBlacklistError';
  }
}

// ブラックリストエントリ
export interface BlacklistEntry {
  jti: string;
  userId: string;
  tenantId?: string;
  tokenType: 'access' | 'refresh';
  revokedAt: number;
  expiresAt: number;
  reason: 'logout' | 'security' | 'admin' | 'expired' | 'rotation';
  deviceFingerprint?: string;
  sessionId?: string;
  ipAddress?: string;
}

// ブラックリスト統計
export interface BlacklistStats {
  totalEntries: number;
  activeEntries: number;
  expiredEntries: number;
  revokedToday: number;
  memoryUsage: number;
  lastCleanup: number;
  performance: {
    averageLookupTime: number;
    totalLookups: number;
    cacheHitRate: number;
  };
}

// リボーク要求
export interface RevocationRequest {
  jti: string;
  reason: BlacklistEntry['reason'];
  userId?: string;
  tenantId?: string;
  sessionId?: string;
  ipAddress?: string;
  batchOperation?: boolean;
}

// メモリキャッシュ管理
class BlacklistMemoryCache {
  private cache = new Map<string, boolean>();
  private stats = {
    hits: 0,
    misses: 0,
    operations: 0,
    lastCleanup: Date.now(),
  };

  // キャッシュチェック
  check(jti: string): boolean | null {
    this.stats.operations++;

    if (this.cache.has(jti)) {
      this.stats.hits++;
      return this.cache.get(jti) || false;
    }

    this.stats.misses++;
    return null; // キャッシュミス
  }

  // キャッシュ追加
  set(jti: string, revoked: boolean): void {
    this.cache.set(jti, revoked);

    // メモリ制限（1000エントリ）
    if (this.cache.size > 1000) {
      this.cleanup();
    }
  }

  // キャッシュクリーンアップ
  private cleanup(): void {
    const now = Date.now();
    const entries = Array.from(this.cache.entries());

    // 古いエントリの半分を削除
    const toDelete = Math.floor(entries.length / 2);
    for (let i = 0; i < toDelete; i++) {
      this.cache.delete(entries[i][0]);
    }

    this.stats.lastCleanup = now;
    logger.debug(`ブラックリストキャッシュクリーンアップ: ${toDelete}件削除`);
  }

  // 統計取得
  getStats() {
    const hitRate = this.stats.operations > 0
      ? (this.stats.hits / this.stats.operations) * 100
      : 0;

    return {
      size: this.cache.size,
      hitRate: Math.round(hitRate * 100) / 100,
      hits: this.stats.hits,
      misses: this.stats.misses,
      operations: this.stats.operations,
      lastCleanup: this.stats.lastCleanup,
    };
  }

  // キャッシュクリア
  clear(): void {
    this.cache.clear();
    this.stats = {
      hits: 0,
      misses: 0,
      operations: 0,
      lastCleanup: Date.now(),
    };
  }
}

// グローバルメモリキャッシュ
const memoryCache = new BlacklistMemoryCache();

/**
 * トークンブラックリストサービス
 */
export class TokenBlacklistService {
  private static readonly STORAGE_KEY = 'salonhub_token_blacklist';
  private static readonly CLEANUP_INTERVAL = 60 * 60 * 1000; // 1時間
  private static lastCleanup = 0;

  /**
   * JTI リボーク（無効化）
   * @param request リボーク要求
   * @returns リボーク結果
   */
  static async revokeToken(request: RevocationRequest): Promise<boolean> {
    try {
      const startTime = performance.now();

      // 既にリボーク済みかチェック
      if (await this.isTokenRevoked(request.jti)) {
        logger.warn(`既にリボーク済みのJTI: ${request.jti}`);
        return true;
      }

      // ブラックリストエントリ作成
      const entry: BlacklistEntry = {
        jti: request.jti,
        userId: request.userId || 'unknown',
        tenantId: request.tenantId,
        tokenType: this.detectTokenType(request.jti),
        revokedAt: Date.now(),
        expiresAt: this.calculateExpiryTime(request.jti),
        reason: request.reason,
        sessionId: request.sessionId,
        ipAddress: request.ipAddress,
      };

      // ストレージ保存
      await this.saveBlacklistEntry(entry);

      // crypto.ts統合（JTI管理）
      revokeJTI(request.jti);

      // メモリキャッシュ更新
      memoryCache.set(request.jti, true);

      const duration = performance.now() - startTime;
      logger.info(`JTIリボーク完了: ${request.jti} (${request.reason}) - ${duration.toFixed(2)}ms`);

      // バッチ操作でなければクリーンアップ実行
      if (!request.batchOperation) {
        this.scheduleCleanup();
      }

      return true;
    } catch (error) {
      logger.error('JTIリボークエラー:', error);
      throw new TokenBlacklistError(
        `JTIリボークに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'REVOCATION_FAILED'
      );
    }
  }

  /**
   * JTI リボーク状態確認（高速）
   * @param jti JWT ID
   * @returns リボーク状態
   */
  static async isTokenRevoked(jti: string): Promise<boolean> {
    try {
      const startTime = performance.now();

      // メモリキャッシュチェック（最高速）
      const cachedResult = memoryCache.check(jti);
      if (cachedResult !== null) {
        return cachedResult;
      }

      // crypto.ts統合チェック
      const cryptoResult = isJTIRevoked(jti);

      // メモリキャッシュ更新
      memoryCache.set(jti, cryptoResult);

      const duration = performance.now() - startTime;

      // パフォーマンス要件チェック（<10ms）
      if (duration > 10) {
        logger.warn(`ブラックリストチェック遅延: ${duration.toFixed(2)}ms (JTI: ${jti})`);
      }

      return cryptoResult;
    } catch (error) {
      logger.error('JTIリボーク状態確認エラー:', error);
      return false; // エラー時は有効とみなす（安全側に倒す）
    }
  }

  /**
   * ユーザーの全トークンリボーク
   * @param userId ユーザーID
   * @param reason リボーク理由
   * @param tenantId テナントID（オプション）
   */
  static async revokeAllUserTokens(
    userId: string,
    reason: BlacklistEntry['reason'] = 'security',
    tenantId?: string
  ): Promise<number> {
    try {
      const blacklist = await this.loadBlacklist();
      const userEntries = blacklist.filter(entry =>
        entry.userId === userId &&
        (!tenantId || entry.tenantId === tenantId) &&
        !entry.revokedAt // まだリボークされていない
      );

      let revokedCount = 0;

      // バッチリボーク実行
      for (const entry of userEntries) {
        await this.revokeToken({
          jti: entry.jti,
          reason,
          userId,
          tenantId,
          batchOperation: true,
        });
        revokedCount++;
      }

      // 最後にクリーンアップ
      this.scheduleCleanup();

      logger.info(`ユーザー全トークンリボーク完了: ${userId} (${revokedCount}件)`);
      return revokedCount;
    } catch (error) {
      logger.error('ユーザー全トークンリボークエラー:', error);
      throw new TokenBlacklistError(
        `ユーザー全トークンリボークに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'REVOCATION_FAILED'
      );
    }
  }

  /**
   * セッションの全トークンリボーク
   * @param sessionId セッションID
   * @param reason リボーク理由
   */
  static async revokeSessionTokens(
    sessionId: string,
    reason: BlacklistEntry['reason'] = 'logout'
  ): Promise<number> {
    try {
      const blacklist = await this.loadBlacklist();
      const sessionEntries = blacklist.filter(entry =>
        entry.sessionId === sessionId && !entry.revokedAt
      );

      let revokedCount = 0;

      for (const entry of sessionEntries) {
        await this.revokeToken({
          jti: entry.jti,
          reason,
          sessionId,
          batchOperation: true,
        });
        revokedCount++;
      }

      this.scheduleCleanup();

      logger.info(`セッション全トークンリボーク完了: ${sessionId} (${revokedCount}件)`);
      return revokedCount;
    } catch (error) {
      logger.error('セッション全トークンリボークエラー:', error);
      throw new TokenBlacklistError(
        `セッション全トークンリボークに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'REVOCATION_FAILED'
      );
    }
  }

  /**
   * 期限切れトークンクリーンアップ
   */
  static async cleanupExpiredTokens(): Promise<number> {
    try {
      const startTime = performance.now();
      const blacklist = await this.loadBlacklist();
      const now = Date.now();

      const beforeCount = blacklist.length;
      const activeBlacklist = blacklist.filter(entry => now < entry.expiresAt);
      const cleanedCount = beforeCount - activeBlacklist.length;

      // ストレージ更新
      await this.saveBlacklist(activeBlacklist);

      // メモリキャッシュクリア（整合性保証）
      memoryCache.clear();

      this.lastCleanup = now;
      const duration = performance.now() - startTime;

      logger.info(`ブラックリストクリーンアップ完了: ${cleanedCount}件削除 - ${duration.toFixed(2)}ms`);
      return cleanedCount;
    } catch (error) {
      logger.error('ブラックリストクリーンアップエラー:', error);
      throw new TokenBlacklistError(
        `ブラックリストクリーンアップに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'CLEANUP_FAILED'
      );
    }
  }

  /**
   * ブラックリスト統計取得
   */
  static async getBlacklistStats(): Promise<BlacklistStats> {
    try {
      const blacklist = await this.loadBlacklist();
      const now = Date.now();
      const todayStart = new Date().setHours(0, 0, 0, 0);

      const activeEntries = blacklist.filter(entry => now < entry.expiresAt);
      const expiredEntries = blacklist.filter(entry => now >= entry.expiresAt);
      const revokedToday = blacklist.filter(entry => entry.revokedAt >= todayStart);

      const cacheStats = memoryCache.getStats();

      return {
        totalEntries: blacklist.length,
        activeEntries: activeEntries.length,
        expiredEntries: expiredEntries.length,
        revokedToday: revokedToday.length,
        memoryUsage: JSON.stringify(blacklist).length,
        lastCleanup: this.lastCleanup,
        performance: {
          averageLookupTime: 5, // 推定値（実装で計測）
          totalLookups: cacheStats.operations,
          cacheHitRate: cacheStats.hitRate,
        },
      };
    } catch (error) {
      logger.error('ブラックリスト統計取得エラー:', error);
      throw new TokenBlacklistError(
        `ブラックリスト統計取得に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'LOOKUP_FAILED'
      );
    }
  }

  /**
   * Redis統合準備（将来的実装）
   */
  static async initializeRedisBackend(redisConfig?: {
    host: string;
    port: number;
    password?: string;
    keyPrefix?: string;
  }): Promise<boolean> {
    try {
      // 将来的にRedisクライアント初期化
      logger.info('Redis ブラックリストバックエンド準備中...');

      if (redisConfig) {
        logger.info(`Redis接続設定: ${redisConfig.host}:${redisConfig.port}`);
        // TODO: Redis接続実装
      }

      // 現在はLocalStorage継続使用
      logger.info('LocalStorage ブラックリストバックエンド使用中');
      return true;
    } catch (error) {
      logger.error('Redis初期化エラー:', error);
      return false;
    }
  }

  // ================================================================
  // プライベート・ヘルパーメソッド
  // ================================================================

  /**
   * ブラックリストエントリ保存
   */
  private static async saveBlacklistEntry(entry: BlacklistEntry): Promise<void> {
    const blacklist = await this.loadBlacklist();
    blacklist.push(entry);
    await this.saveBlacklist(blacklist);
  }

  /**
   * ブラックリスト読み込み
   */
  private static async loadBlacklist(): Promise<BlacklistEntry[]> {
    try {
      const blacklistStr = localStorage.getItem(this.STORAGE_KEY);
      return blacklistStr ? JSON.parse(blacklistStr) : [];
    } catch (error) {
      logger.error('ブラックリスト読み込みエラー:', error);
      return [];
    }
  }

  /**
   * ブラックリスト保存
   */
  private static async saveBlacklist(blacklist: BlacklistEntry[]): Promise<void> {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(blacklist));
  }

  /**
   * トークン種別検出
   */
  private static detectTokenType(jti: string): 'access' | 'refresh' {
    // JTI形式やペイロード解析でトークン種別判定
    // 簡易実装：JTIの特徴で判定
    return jti.includes('refresh') ? 'refresh' : 'access';
  }

  /**
   * 有効期限計算
   */
  private static calculateExpiryTime(jti: string): number {
    // 実際のJWTペイロードから有効期限を取得
    // 簡易実装：デフォルト有効期限設定
    const tokenType = this.detectTokenType(jti);
    const defaultExpiry = tokenType === 'access'
      ? 15 * 60 * 1000     // 15分
      : 30 * 24 * 60 * 60 * 1000; // 30日

    return Date.now() + defaultExpiry;
  }

  /**
   * クリーンアップスケジュール
   */
  private static scheduleCleanup(): void {
    const now = Date.now();
    if (now - this.lastCleanup > this.CLEANUP_INTERVAL) {
      // 非同期でクリーンアップ実行
      setTimeout(() => {
        this.cleanupExpiredTokens().catch(error => {
          logger.error('スケジュールクリーンアップエラー:', error);
        });
      }, 100);
    }
  }
}

/**
 * ブラックリスト管理便利関数
 */

/**
 * 緊急全トークンリボーク（セキュリティインシデント用）
 * @param reason リボーク理由
 */
export const emergencyRevokeAllTokens = async (reason: string = 'security_incident'): Promise<number> => {
  try {
    logger.warn(`緊急全トークンリボーク実行: ${reason}`);

    const blacklist = await TokenBlacklistService['loadBlacklist']();
    let revokedCount = 0;

    // 全アクティブトークンをリボーク
    for (const entry of blacklist) {
      if (!entry.revokedAt && Date.now() < entry.expiresAt) {
        await TokenBlacklistService.revokeToken({
          jti: entry.jti,
          reason: 'security',
          batchOperation: true,
        });
        revokedCount++;
      }
    }

    logger.warn(`緊急全トークンリボーク完了: ${revokedCount}件`);
    return revokedCount;
  } catch (error) {
    logger.error('緊急全トークンリボークエラー:', error);
    throw error;
  }
};

/**
 * ブラックリスト情報取得（デバッグ用）
 */
export const getBlacklistInfo = () => {
  if (import.meta.env.PROD) {
    return { error: 'Production環境では利用できません' };
  }

  return {
    implementation: 'LocalStorage + メモリキャッシュ',
    redisReady: false,
    performanceTarget: '<10ms',
    features: [
      'JTI ベースリボーク',
      'メモリキャッシュ最適化',
      'バッチ処理対応',
      '自動クリーンアップ',
      '統計・監視機能',
      'Redis準備済み'
    ],
    cacheStats: memoryCache.getStats(),
    timestamp: new Date().toISOString(),
  };
};

// 開発環境でのテスト用エクスポート
if (import.meta.env.DEV) {
  (window as typeof window & { SalonHubTokenBlacklist: object }).SalonHubTokenBlacklist = {
    service: TokenBlacklistService,
    emergency: emergencyRevokeAllTokens,
    getInfo: getBlacklistInfo,
  };
}